import {type TimeZone} from '@hconnect/common/types'
import {dateFormatter, selectableTableRowSx} from '@hconnect/uikit'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {Box, Grid, Stack, Typography} from '@mui/material'
import moment from 'moment-timezone'

import {useWidgetTranslation} from '../hooks'
import type {ChecklistTemplate} from '../types'

type Props = {
  data?: ChecklistTemplate[]
  selectedTemplateId?: string
  onTemplateClick: (template: ChecklistTemplate) => void
  timezone: TimeZone
}

export const ChecklistTemplatesList = ({
  data,
  selectedTemplateId,
  onTemplateClick,
  timezone
}: Props) => {
  return (
    <Stack spacing={2}>
      {data?.map((checklist) => (
        <ChecklistTemplateListItem
          key={checklist.id}
          item={checklist}
          selected={selectedTemplateId === checklist.id}
          onClick={() => onTemplateClick(checklist)}
          timezone={timezone}
        />
      ))}
    </Stack>
  )
}

const ChecklistTemplateListItem = ({
  item,
  selected,
  onClick,
  timezone
}: {
  item: ChecklistTemplate
  selected: boolean
  onClick?: () => void
  timezone: TimeZone
}) => {
  const {t} = useWidgetTranslation()

  return (
    <Box sx={{bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1}} onClick={onClick}>
      <CardBox sx={selectableTableRowSx(selected)}>
        <Stack spacing={1.5}>
          <Typography variant="h4">{item.name}</Typography>
          <Typography>{item.description}</Typography>
          <Grid container columns={{xs: 2, md: 4}} rowSpacing={1.5}>
            <GridDataCell
              label={t('label.type')}
              value={t(`checklist.templateType.${item.type}`)}
            />
            <GridDataCell
              label={t('label.category')}
              value={t(`checklist.templateCategory.${item.category}`)}
            />
            <GridDataCell
              label={t('label.addedToTheSystem')}
              value={dateFormatter(moment.utc(item.createdAt).tz(timezone))}
            />
          </Grid>
        </Stack>
      </CardBox>
    </Box>
  )
}

const GridDataCell = ({label, value}: {label: string; value: string}) => (
  <Grid item xs={1} sm={1} md={1}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography>{value}</Typography>
  </Grid>
)
